import type { CultureEntryInput, CultureResult, Susceptibility } from '#shared/types';
import type { EntryProgress } from './result-entry.logic';

export type CultureDraft = CultureEntryInput;

/** The editable working copy of a culture, panel in print order. */
export function initialCulture(stored: CultureResult): CultureDraft {
  return {
    specimen: stored.specimen,
    outcome: stored.outcome,
    organism_id: stored.organism_id,
    growth: stored.growth,
    colony_count: stored.colony_count,
    sensitivities: stored.sensitivities.map((row) => ({
      antibiotic_id: row.antibiotic_id,
      result: row.result,
    })),
  };
}

/**
 * Whether the culture can be completed, by the server's rule: an outcome, and
 * for a growth an organism and at least one antibiotic read. Counted as one
 * result, entered once it is complete, so the action bar and Ctrl+Enter work
 * as on any report.
 */
export function cultureProgress(draft: CultureDraft): EntryProgress {
  const ready =
    draft.outcome === 'no_growth' ||
    (draft.outcome === 'growth' &&
      draft.organism_id !== null &&
      draft.sensitivities.some((row) => row.result !== null));
  return { entered: ready ? 1 : 0, total: 1, allEntered: ready };
}

export function withSensitivity(
  draft: CultureDraft,
  antibioticId: number,
  result: Susceptibility | null,
): CultureDraft {
  return {
    ...draft,
    sensitivities: draft.sensitivities.map((row) =>
      row.antibiotic_id === antibioticId ? { ...row, result } : row,
    ),
  };
}

/** Adds an antibiotic to the end of the panel, unread. A repeat is ignored. */
export function withAntibiotic(draft: CultureDraft, antibioticId: number): CultureDraft {
  if (draft.sensitivities.some((row) => row.antibiotic_id === antibioticId)) return draft;
  return {
    ...draft,
    sensitivities: [...draft.sensitivities, { antibiotic_id: antibioticId, result: null }],
  };
}

export function withoutAntibiotic(draft: CultureDraft, antibioticId: number): CultureDraft {
  return {
    ...draft,
    sensitivities: draft.sensitivities.filter((row) => row.antibiotic_id !== antibioticId),
  };
}
